// IPRegulatoryPathwayPage.jsx
// ✅ 2 cards per row (Desktop + Tablet), 1 on Mobile
// ✅ Center content (cards + text) like you asked
// ✅ Same FludeeProductPage style (Hero + overlay + premium cards + subtle fadeUp)
// ✅ No overlap (shadow buffer + safe hover)

import * as React from "react";
import bg from "../assets/backtgch.png";

import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";

/* ---------------- DATA ---------------- */
const DATA = [
  {
    title: "Outline Intellectual Property",
    icon: <ShieldOutlinedIcon />,
    chips: ["Ownership", "FTO", "Roadmap"],
    items: [
      "Identify core innovations (methods, compositions, devices, software, algorithms).",
      "Map freedom-to-operate considerations and potential third-party IP risks.",
      "Plan IP ownership structure and assignments.",
      "Develop an IP roadmap aligned with product milestones.",
    ],
  },
  {
    title: "Patent Filings",
    icon: <DescriptionOutlinedIcon />,
    chips: ["Provisional", "PCT", "Claims"],
    items: [
      "Provisional filings for early priority.",
      "PCT planning for global coverage.",
      "Claims strategy for core protection.",
      "Maintain invention disclosure records.",
    ],
  },
  {
    title: "Confidentiality Practices",
    icon: <LockOutlinedIcon />,
    chips: ["NDA", "Access Control", "Security"],
    items: [
      "NDAs with employees, vendors, and partners.",
      "Access control for data, code, and designs.",
      "Secure handling of demos and documents.",
      "Controlled sharing and audit trails.",
    ],
  },
  {
    title: "Regulatory Pathway",
    icon: <TimelineOutlinedIcon />,
    chips: ["Pathway", "Evidence", "QMS"],
    items: [
      "Determine product classification.",
      "Select pathway: 510(k), De Novo, PMA, CE.",
      "Plan verification & validation.",
      "Prepare quality system documentation.",
    ],
  },
];

/* ---------------- Bullet List (CENTER) ---------------- */
function BulletList({ items }) {
  return (
    <List dense sx={{ py: 0 }}>
      {items.map((t, i) => (
        <ListItem
          key={i}
          disableGutters
          sx={{ px: 0, alignItems: "flex-start", justifyContent: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 28, mt: "3px", justifyContent: "center" }}>
            <CheckCircleOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t}
            primaryTypographyProps={{
              sx: { fontSize: 14, lineHeight: 1.55, textAlign: "center" },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

/* ---------------- Premium Card (CENTER content) ---------------- */
function FeatureCard({ icon, title, chips = [], items, delayMs = 0 }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        p: 3,
        borderRadius: 4,
        position: "relative",
        overflow: "hidden",
        border: "none",
        bgcolor: alpha(theme.palette.background.paper, isDark ? 0.92 : 1),

        display: "flex",
        flexDirection: "column",
        minHeight: 320,

        boxShadow: isDark
          ? `0 12px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)`
          : `0 14px 45px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255,255,255,0.9)`,

        opacity: 0,
        transform: "translateY(16px)",
        animation: "fadeUp 700ms ease forwards",
        animationDelay: `${delayMs}ms`,

        transition: "transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease",
        "&:hover": {
          transform: { xs: "none", md: "translateY(-2px)" },
          zIndex: 2,
          boxShadow: isDark
            ? `0 20px 70px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.06)`
            : `0 22px 70px rgba(15, 23, 42, 0.18), inset 0 1px 0 rgba(255,255,255,1)`,
        },

        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `radial-gradient(
            520px 220px at 20% 0%,
            ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)},
            transparent 65%
          )`,
          pointerEvents: "none",
        },

        "@keyframes fadeUp": {
          from: { opacity: 0, transform: "translateY(16px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            color: "primary.main",
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
          }}
        >
          {icon}
        </Box>

        <Typography sx={{ fontWeight: 900, fontSize: 18, lineHeight: 1.15, textAlign: "center" }}>
          {title}
        </Typography>
      </Stack>

      {chips?.length ? (
        <Stack direction="row" flexWrap="wrap" gap={0.9} sx={{ mt: 1.2 }} justifyContent="center">
          {chips.map((c) => (
            <Chip
              key={c}
              label={c}
              size="small"
              sx={{
                fontWeight: 800,
                borderRadius: 999,
                border: "none",
                bgcolor: isDark ? alpha("#fff", 0.08) : alpha("#0A0F1A", 0.06),
              }}
            />
          ))}
        </Stack>
      ) : null}

      <Divider sx={{ my: 1.6, opacity: 0.6 }} />

      <Box sx={{ flex: 1 }}>
        <BulletList items={items} />
      </Box>
    </Paper>
  );
}

/* ---------------- Page ---------------- */
export default function IPRegulatoryPathwayPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          py: { xs: 5, md: 8 },
          backgroundImage: `url(${bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: isDark ? alpha("#000", 0.75) : alpha("#fff", 0.85),
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
              <Chip label="Compliance-ready" color="primary" />
              <Chip icon={<GavelOutlinedIcon />} label="IP + Regulatory" variant="outlined" />
              <Chip icon={<ShieldOutlinedIcon />} label="Risk controls" variant="outlined" />
            </Stack>

            <Typography component="h1" sx={{ fontWeight: 950, fontSize: "clamp(30px,5vw,54px)" }}>
              IP &{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                Regulatory Pathway
              </Box>
            </Typography>

            <Typography sx={{ color: "text.secondary", fontSize: 15, maxWidth: 900 }}>
              A clear, practical outline of intellectual property strategy, confidentiality practices, and the
              regulatory pathway to support development and commercialization.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* CARDS (2 rows, centered) */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid
          container
          justifyContent="center"              // ✅ center grid
          columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}
          rowSpacing={{ xs: 2.6, sm: 3.2, md: 3.6 }}
          sx={{ alignItems: "stretch", overflow: "visible" }}
        >
          {DATA.map((c, i) => (
            <Grid key={c.title} item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
              {/* shadow buffer so overlap never happens */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  pb: { xs: 2.2, sm: 2.6, md: 3 },
                }}
              >
                <FeatureCard
                  icon={c.icon}
                  title={c.title}
                  chips={c.chips}
                  items={c.items}
                  delayMs={i * 90}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
